import { Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { categories, heroSlides, hotShows, newShows, shows, faqs } from '@/mocks/shows';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ShowCard from '@/components/base/ShowCard';

export default function Home() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [faqOpen, setFaqOpen] = useState<number | null>(null);

  // Auto rotate hero
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const hero = heroSlides[currentSlide];

  // Schema: Organization + WebSite + WebPage
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '屁屁影院',
        url: 'https://pipicinema.com',
        description: '电视剧全集免费在线观看不缓冲，最新热播同步剧集免费追，高清流畅剧集完整，影视大全每天更新一站享用',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'contact@pipicinema.com',
          contactType: 'customer service',
        },
      },
      {
        '@type': 'WebSite',
        name: '屁屁影院',
        url: 'https://pipicinema.com',
        potentialAction: {
          '@type': 'SearchAction',
          'target': 'https://pipicinema.com/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '屁屁影院 - 首页',
        description: '屁屁影院电视剧全集免费在线看，不缓冲高清秒播，最新热播剧集同步更新，剧集完整一追到底',
        url: 'https://pipicinema.com',
        isPartOf: { '@type': 'WebSite', name: '屁屁影院', url: 'https://pipicinema.com' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqs.map((faq) => ({
          '@type': 'Question',
          name: faq.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.a,
          },
        })),
      },
    ],
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
      />

      {/* ===== Hero Section ===== */}
      <section className="relative w-full h-[500px] md:h-[620px] overflow-hidden">
        {/* Background Slides */}
        {heroSlides.map((slide, i) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-800 ${
              i === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
          >
            <img
              src={slide.image}
              alt={slide.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        ))}
        {/* Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/30 to-foreground-950/70"></div>

        {/* Hero Content */}
        <div className="relative z-10 w-full h-full flex flex-col items-center justify-center px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-heading text-3xl md:text-5xl text-background-50 mb-4 leading-tight">
              <a href="/" className="text-background-50 hover:text-primary-300 transition-colors">
                屁屁影院
              </a>
            </h1>
            <p className="text-base md:text-lg text-background-50/80 mb-3 leading-relaxed">
              电视剧全集免费在线观看不缓冲
            </p>
            <p className="text-sm md:text-base text-background-50/60 mb-8 leading-relaxed max-w-xl mx-auto">
              最新热播同步剧集免费追，高清流畅剧集完整，影视大全每天更新一站享用
            </p>
            {/* Current Slide Info */}
            <div className="mb-6 flex items-center justify-center gap-4">
              <span className="text-2xl md:text-3xl font-heading text-background-50">{hero.title}</span>
              <span className="px-2 py-0.5 text-xs bg-primary-500 text-background-50 rounded-md whitespace-nowrap">
                {hero.rating}
              </span>
            </div>
            <p className="text-sm text-background-50/70 max-w-md mx-auto mb-6 line-clamp-2">
              {hero.description}
            </p>
            <Link
              to={`/article/${hero.id}`}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-all text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              <i className="ri-play-fill text-lg w-5 h-5 flex items-center justify-center"></i>
              立即观看
            </Link>

            {/* Slide Dots */}
            <div className="flex items-center justify-center gap-2 mt-8">
              {heroSlides.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrentSlide(i)}
                  className={`w-2 h-2 rounded-full transition-all cursor-pointer whitespace-nowrap ${
                    i === currentSlide
                      ? 'bg-primary-500 w-6'
                      : 'bg-background-50/40 hover:bg-background-50/70'
                  }`}
                  aria-label={`第${i + 1}张`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== Main Content ===== */}
      <main className="max-w-[1440px] mx-auto px-4 md:px-6">
        {/* ===== Category Quick Nav ===== */}
        <section className="py-8 md:py-10">
          <div className="flex items-center flex-wrap gap-2 justify-center">
            <Link
              to="/"
              className="px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              全部
            </Link>
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="px-5 py-2 bg-background-100 text-foreground-700 rounded-full text-sm hover:bg-secondary-100 hover:text-secondary-900 transition-colors cursor-pointer whitespace-nowrap"
              >
                {cat.name}
              </Link>
            ))}
          </div>
        </section>

        {/* ===== Hot Recommendations ===== */}
        <section className="mb-10 md:mb-14">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
              热门推荐
            </h2>
            <Link
              to="/category/dianshiju"
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
            >
              查看更多
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {hotShows.map((show) => (
              <ShowCard key={show.id} show={show} />
            ))}
          </div>
        </section>

        {/* ===== Latest Updates ===== */}
        <section className="mb-10 md:mb-14">
          <div className="flex items-center justify-between mb-5">
            <h2 className="font-heading text-xl md:text-2xl text-foreground-950">
              最新更新
            </h2>
            <Link
              to="/category/zongyi"
              className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
            >
              查看更多
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {newShows.map((show) => (
              <ShowCard key={show.id} show={show} />
            ))}
          </div>
        </section>

        {/* ===== By Category ===== */}
        {categories.slice(0, 3).map((cat) => {
          const items = shows.filter((s) => s.categorySlug === cat.slug).slice(0, 4);
          if (items.length === 0) return null;
          return (
            <section key={cat.slug} className="mb-10 md:mb-14">
              <div className="flex items-center justify-between mb-5">
                <h2 className="font-heading text-xl md:text-2xl text-foreground-950 flex items-center gap-2">
                  <i className={`${cat.icon} text-primary-500 w-6 h-6 flex items-center justify-center`}></i>
                  {cat.name}
                </h2>
                <Link
                  to={`/category/${cat.slug}`}
                  className="text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer flex items-center gap-1 whitespace-nowrap"
                >
                  查看更多
                  <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                {items.map((show) => (
                  <ShowCard key={show.id} show={show} />
                ))}
              </div>
            </section>
          );
        })}

        {/* ===== FAQ ===== */}
        <section className="mb-10 md:mb-14">
          <h2 className="font-heading text-xl md:text-2xl text-foreground-950 mb-6 text-center">
            常见问题
          </h2>
          <div className="max-w-2xl mx-auto space-y-3">
            {faqs.map((faq, i) => (
              <div
                key={i}
                className="bg-background-100 rounded-lg overflow-hidden"
              >
                <button
                  onClick={() => setFaqOpen(faqOpen === i ? null : i)}
                  className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer whitespace-nowrap"
                >
                  <span className="text-sm font-medium text-foreground-900 pr-4 whitespace-normal">
                    {faq.q}
                  </span>
                  <i
                    className={`ri-arrow-down-s-line text-lg text-foreground-500 transition-transform duration-200 w-5 h-5 flex items-center justify-center flex-shrink-0 ${
                      faqOpen === i ? 'rotate-180' : ''
                    }`}
                  ></i>
                </button>
                {faqOpen === i && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {faq.a}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* ===== CTA Banner ===== */}
        <section className="mb-10 md:mb-14 relative rounded-2xl overflow-hidden h-[200px] md:h-[240px]">
          <img
            src="https://readdy.ai/api/search-image?query=Cinematic%20dark%20theater%20with%20warm%20glowing%20screen%20and%20comfortable%20red%20seats%2C%20atmospheric%20movie%20watching%20experience%2C%20stylized%20illustration%20with%20rich%20contrast%2C%20inviting%20and%20cozy%20vibe&width=1600&height=480&seq=cta-banner-home&orientation=landscape"
            alt="免费追剧"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-foreground-950/60 flex flex-col items-center justify-center px-4">
            <h3 className="font-heading text-xl md:text-2xl text-background-50 mb-2">
              随时随地，免费追剧
            </h3>
            <p className="text-sm text-background-50/70 mb-4 text-center max-w-md">
              50000+ 影视资源，每天更新，高清不缓冲，剧集完整一追到底
            </p>
            <Link
              to="/category/dianshiju"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-500 text-background-50 rounded-lg hover:bg-accent-600 transition-all text-sm font-medium cursor-pointer whitespace-nowrap"
            >
              开始追剧
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
            </Link>
          </div>
        </section>
      </main>
    </>
  );
}